import React, { useState } from 'react';
import { Typography, IconButton, Box, CircularProgress, Alert, Chip } from '@mui/material';
import { AccountBalance, Smartphone, Language, CreditCard, Business } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { RootState } from '../../redux/root-reducer';
import { deleteExternalAccount } from '../../redux/account/actions';
import { IExternalAccount } from '../../redux/account/types';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { displayAccountCurrency, displayCountryLabel } from '../../utils/externalAccountDisplay';
import { verificationStatusDisplay, payoutProcessorLabel } from '../../utils/payoutDisplay';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditAchAccountModal from './EditAchAccountModal';

function externalAccountId(account: IExternalAccount): string {
    return String(account._id ?? account.id ?? '');
}

interface ExternalAccountListProps {
    accounts: IExternalAccount[];
}

const valueMutedSx = {
    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
    fontFamily: FONT_ENUM.MAIN_REGULAR,
    fontWeight: 500,
    letterSpacing: { xs: '0.02em', sm: '0.03em' },
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
};

const ExternalAccountList: React.FC<ExternalAccountListProps> = ({ accounts }) => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const { deletingExternalAccount } = useSelector((state: RootState) => state.account);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState<string | null>(null);
    const [accountToEdit, setAccountToEdit] = useState<IExternalAccount | null>(null);

    const handleDeleteClick = (accountId: string) => {
        setAccountToDelete(accountId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (accountToDelete) {
            dispatch(deleteExternalAccount(accountToDelete));
        }
        setDeleteDialogOpen(false);
        setAccountToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAccountToDelete(null);
    };

    const accountIconSx = {
        fontSize: { xs: 26, sm: 30, md: 34, lg: 40 },
    };

    const getAccountIcon = (accountType: string) => {
        switch (accountType) {
            case 'nuban':
                return <CreditCard sx={accountIconSx} />;
            case 'mobile_money':
            case 'mobile_money_business':
                return <Smartphone sx={accountIconSx} />;
            case 'bank_account':
                return <AccountBalance sx={accountIconSx} />;
            case 'ach':
                return <AccountBalance sx={accountIconSx} />;
            case 'paypal':
                return <Business sx={accountIconSx} />;
            case 'swift':
                return <Language sx={accountIconSx} />;
            default:
                return <AccountBalance sx={accountIconSx} />;
        }
    };

    const getAccountTypeLabel = (accountType: string) => {
        switch (accountType) {
            case 'nuban':
                return 'NUBAN';
            case 'mobile_money_business':
                return 'M-Pesa Business';
            case 'mobile_money':
                return 'Mobile Money';
            case 'bank_account':
                return 'Bank Account';
            case 'ach':
                return 'ACH';
            case 'paypal':
                return 'PayPal';
            case 'swift':
                return 'SWIFT';
            default:
                return accountType;
        }
    };

    const getAccountDetails = (
        account: IExternalAccount,
    ): {
        primary: string;
        secondary: string;
        code?: string;
        codeLabel?: string;
    } => {
        switch (account.external_account_type) {
            case 'nuban':
                return {
                    primary: account.bank_details?.bank_name || 'NUBAN Account',
                    secondary: `Account: ${account.bank_details?.account_number}`,
                    code: account.bank_details?.bank_code,
                    codeLabel: 'Bank Code',
                };
            case 'mobile_money':
                return {
                    primary: account.mobile_money_details?.mobile_money_name || 'Mobile Money',
                    secondary: `Phone: ${account.mobile_money_details?.phone_number}`,
                    code: account.mobile_money_details?.mobile_money_code,
                    codeLabel: 'Bank Code',
                };
            case 'bank_account':
                return {
                    primary: 'Wire Transfer Account',
                    secondary: `Account: ${account.bank_details?.account_number}`,
                    code: account.bank_details?.routing_number?.toString(),
                    codeLabel: 'Routing Number',
                };
            case 'ach':
                return {
                    primary: account.bank_details?.bank_name || 'ACH Transfer',
                    secondary: `Account: ${account.bank_details?.account_number}`,
                    code: account.bank_details?.routing_number?.toString(),
                    codeLabel: 'Routing Number',
                };
            case 'paypal':
                return {
                    primary: account.paypal_details?.username || 'PayPal Account',
                    secondary: account.paypal_details?.email || '',
                    code: 'PayPal',
                    codeLabel: 'Bank Code',
                };
            default:
                return {
                    primary: account.name,
                    secondary: 'External Account',
                    code: account.external_account_type,
                    codeLabel: 'Bank Code',
                };
        }
    };

    if (accounts.length === 0) {
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                No external accounts found. Add your first account to get started.
            </Alert>
        );
    }

    const maxAccounts = 3;
    const accountCount = accounts.length;
    const progress = Math.min(accountCount / maxAccounts, 1);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ alignSelf: 'flex-start', width: '100%', maxWidth: 280, mb: 0.5 }}>
                <Typography
                    variant="h6"
                    sx={{
                        mb: 1.5,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: t.tag_white,
                        textAlign: 'left',
                    }}>
                    Your External Accounts ({accountCount}/{maxAccounts})
                </Typography>
                <Box
                    sx={{
                        width: '100%',
                        height: 4,
                        borderRadius: 999,
                        bgcolor: alpha(t.tag_white, 0.12),
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            width: `${progress * 100}%`,
                            height: '100%',
                            borderRadius: 999,
                            bgcolor: t.highlight,
                            transition: 'width 0.35s ease',
                        }}
                    />
                </Box>
            </Box>
            {accounts.map((account, index) => {
                const details = getAccountDetails(account);
                const codeDisplay = details.code ? String(details.code) : '—';
                return (
                    <Box
                        key={externalAccountId(account) || `account-${index}`}
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            gap: { xs: 2, sm: 2.5, md: 3 },
                            p: { xs: 2, sm: 2.5, md: 3 },
                            borderRadius: 3,
                            bgcolor: 'rgba(39, 39, 42, 0.35)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            boxSizing: 'border-box',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            transition: 'background-color 0.35s ease, border-color 0.35s ease',
                            '&:hover': {
                                bgcolor: 'rgba(39, 39, 42, 0.5)',
                            },
                            '&:hover .ext-bank-icon-wrap': {
                                transform: 'scale(1.04)',
                            },
                            '@media (min-width: 900px) and (max-width: 1200px)': {
                                p: 2,
                                gap: 2,
                            },
                        }}>
                        <Box
                            className="ext-bank-icon-wrap"
                            sx={{
                                width: { xs: 48, sm: 56, md: 60, lg: 64 },
                                height: { xs: 48, sm: 56, md: 60, lg: 64 },
                                borderRadius: 2,
                                flexShrink: 0,
                                alignSelf: { xs: 'center', md: 'center' },
                                bgcolor: 'rgba(0,0,0,0.28)',
                                border: '1px solid rgba(255,255,255,0.06)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: t.highlight,
                                boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.35)',
                                transition: 'transform 0.35s ease',
                                '@media (min-width: 900px) and (max-width: 1200px)': {
                                    width: 50,
                                    height: 50,
                                },
                            }}>
                            {getAccountIcon(account.external_account_type)}
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                maxWidth: '100%',
                                px: { xs: 0 },
                                pr: { sm: 6.5, md: 1 },
                                '@media (min-width: 900px) and (max-width: 1200px)': {
                                    pr: 0,
                                },
                            }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'minmax(0, 1fr)',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                        md: 'minmax(0, 1fr)',
                                        lg: 'repeat(2, minmax(0, 1fr))',
                                        xl: 'repeat(4, minmax(0, 1fr))',
                                    },
                                    gap: { xs: 2, sm: 2.25, md: 2.5 },
                                    alignItems: 'start',
                                    textAlign: { xs: 'center', md: 'left' },
                                    justifyItems: { xs: 'center', md: 'stretch' },
                                    width: '100%',
                                    maxWidth: '100%',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        alignItems: { xs: 'center', md: 'stretch' },
                                        textAlign: { xs: 'center', md: 'left' },
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '9px', sm: '10px' },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            color: t.tag_white,
                                            opacity: 0.45,
                                            textTransform: 'uppercase',
                                            letterSpacing: { xs: '0.08em', sm: '0.12em' },
                                        }}>
                                        Bank Name
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.125rem' },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            color: t.primary_white,
                                            lineHeight: 1.25,
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                            '@media (min-width: 900px) and (max-width: 1200px)': {
                                                fontSize: '0.95rem',
                                            },
                                        }}>
                                        {details.primary}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: t.highlight,
                                            opacity: 0.9,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                        }}>
                                        {getAccountTypeLabel(account.external_account_type)}
                                    </Typography>
                                    {account.verification_status && (
                                        <Chip
                                            size="small"
                                            label={verificationStatusDisplay(account.verification_status).label}
                                            color={verificationStatusDisplay(account.verification_status).color}
                                            sx={{
                                                mt: 0.5,
                                                alignSelf: { xs: 'center', md: 'flex-start' },
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.65rem',
                                            }}
                                        />
                                    )}
                                    {account.payout_processor && (
                                        <Typography
                                            sx={{
                                                fontSize: '0.65rem',
                                                color: t.light_gray_text,
                                                mt: 0.5,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            }}>
                                            {payoutProcessorLabel(account.payout_processor, account.stripe_region)}
                                            {account.verified_account_name ? ` · ${account.verified_account_name}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        alignItems: { xs: 'center', md: 'stretch' },
                                        textAlign: { xs: 'center', md: 'left' },
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '9px', sm: '10px' },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            color: t.tag_white,
                                            opacity: 0.45,
                                            textTransform: 'uppercase',
                                            letterSpacing: { xs: '0.08em', sm: '0.12em' },
                                        }}>
                                        Account Number
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...valueMutedSx,
                                            color: t.tag_white,
                                            opacity: 0.85,
                                        }}>
                                        {details.secondary}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        alignItems: { xs: 'center', md: 'stretch' },
                                        textAlign: { xs: 'center', md: 'left' },
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '9px', sm: '10px' },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            color: t.tag_white,
                                            opacity: 0.45,
                                            textTransform: 'uppercase',
                                            letterSpacing: { xs: '0.08em', sm: '0.12em' },
                                        }}>
                                        {details.codeLabel || 'Bank Code'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...valueMutedSx,
                                            color: t.tag_white,
                                            opacity: 0.85,
                                        }}>
                                        {details.codeLabel === 'Routing Number'
                                            ? `Routing: ${codeDisplay}`
                                            : `Code: ${codeDisplay}`}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        alignItems: { xs: 'center', md: 'flex-start' },
                                        textAlign: { xs: 'center', md: 'left' },
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: '9px', sm: '10px' },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 700,
                                            color: t.tag_white,
                                            opacity: 0.45,
                                            textTransform: 'uppercase',
                                            letterSpacing: { xs: '0.08em', sm: '0.12em' },
                                        }}>
                                        Currency
                                    </Typography>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            alignSelf: { xs: 'center', md: 'flex-start' },
                                            maxWidth: '100%',
                                            px: { xs: 1.25, sm: 1.5 },
                                            py: 0.5,
                                            borderRadius: 999,
                                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 800,
                                            color: t.highlight,
                                            bgcolor: alpha(t.highlight, 0.12),
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.06em',
                                            boxSizing: 'border-box',
                                            wordBreak: 'break-all',
                                        }}>
                                        {displayAccountCurrency(account)}
                                    </Box>
                                </Box>
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: t.tag_white,
                                    opacity: 0.5,
                                    textAlign: { xs: 'center', md: 'left' },
                                    mt: { xs: 1.5, md: 2 },
                                    wordBreak: 'break-word',
                                }}>
                                {displayCountryLabel(account.country)}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'row', md: 'column' },
                                position: { xs: 'absolute', md: 'static' },
                                top: { xs: 8, md: 'auto' },
                                right: { xs: 8, md: 'auto' },
                                gap: 0.5,
                                flexShrink: 0,
                            }}>
                            {account.external_account_type === 'ach' && (
                                <IconButton
                                    aria-label="edit account"
                                    onClick={() => setAccountToEdit(account)}
                                    size="small"
                                    sx={{
                                        color: t.tag_white,
                                        opacity: 0.7,
                                        '&:hover': {
                                            color: t.highlight,
                                            bgcolor: 'transparent',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            fontSize: { xs: 20, sm: 22 },
                                        },
                                    }}>
                                    <EditOutlinedIcon />
                                </IconButton>
                            )}
                            <IconButton
                                aria-label="delete account"
                                onClick={() => {
                                    const id = externalAccountId(account);
                                    if (id) handleDeleteClick(id);
                                }}
                                disabled={deletingExternalAccount}
                                size="small"
                                sx={{
                                    color: t.tag_white,
                                    opacity: 0.7,
                                    '&:hover': {
                                        color: 'error.main',
                                        bgcolor: 'transparent',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        fontSize: { xs: 20, sm: 22 },
                                    },
                                }}>
                                {deletingExternalAccount ? (
                                    <CircularProgress size={20} sx={{ color: t.tag_white }} />
                                ) : (
                                    <DeleteOutlinedIcon />
                                )}
                            </IconButton>
                        </Box>
                    </Box>
                );
            })}
            <EditAchAccountModal
                open={Boolean(accountToEdit)}
                account={accountToEdit}
                onClose={() => setAccountToEdit(null)}
            />
            <DialogueSheet
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onAgreeClicked={handleDeleteConfirm}
                onDisagreeClick={handleDeleteCancel}
                header="Delete Account"
                body="Are you sure you want to delete this account? This action cannot be undone."
                positiveText="Delete"
                negativeText="Cancel"
                positiveTextStyle={{ color: '#f44336' }}
            />
        </Box>
    );
};

export default ExternalAccountList;
